import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios'
import { isNil } from 'lodash'
import { trackPromise } from 'react-promise-tracker'
import createAuthRefreshInterceptor from 'axios-auth-refresh'

const JWT_KEY = 'wb-jwt'

const navigateToUnauthorized = () => (location.href = '/unauthorized')
declare module 'axios' {
	// eslint-disable-next-line
	interface AxiosResponse<T = any> extends Promise<T> {}
}
export abstract class HttpClient {
	private instance: AxiosInstance

	private refreshAuthLogic = (error: AxiosError) => {
		const wb_jwt = localStorage.getItem(JWT_KEY)
		if (!wb_jwt) {
			navigateToUnauthorized()
			return Promise.reject(error)
		}

		if (error.response.headers['is-token-expired']) {
			// try refresh token
			return this.instance
				.post<any>('/auth/refresh-token', { accessToken: wb_jwt })
				.then((newToken) => {
					localStorage.setItem(JWT_KEY, newToken as any)
					return Promise.resolve()
				})
				.catch((error) => {
					location.href = '/unauthorized'
					return Promise.reject(error)
				})
		}
		navigateToUnauthorized()
		return Promise.reject(error)
	}

	public get<T>(url: string, config?: AxiosRequestConfig<any>, area?: string) {
		return trackPromise(this.instance.get<T>(url, config), area)
	}
	public post<T>(url: string, body?: any, area?: string) {
		return trackPromise(this.instance.post<T>(url, body), area)
	}
	public put<T>(url: string, body?: any, area?: string) {
		return trackPromise(this.instance.put<T>(url, body), area)
	}
	public delete<T>(url: string, config?: AxiosRequestConfig<any>, area?: string) {
		return trackPromise(this.instance.delete<T>(url, config), area)
	}

	public constructor(baseURL: string) {
		this.instance = axios.create({
			baseURL,
		})

		createAuthRefreshInterceptor(this.instance, this.refreshAuthLogic, { statusCodes: [401] })
		this._initializeRequestInterceptor()
		this._initializeResponseInterceptor()
	}

	private _initializeResponseInterceptor = () => {
		this.instance.interceptors.response.use(this._handleResponse, this._handleError)
	}

	private _initializeRequestInterceptor = () => {
		this.instance.interceptors.request.use(this._handleRequest, this._handleError)

		// get refresh token if not found
		this.instance.post<any>('/auth/generate-refresh-token')
	}

	private _handleRequest = (config: AxiosRequestConfig) => {
		if (isNil(config.headers)) {
			return (config.headers = {})
		}
		config.headers['Content-Type'] = 'application/json'
		const wb_jwt = localStorage.getItem(JWT_KEY)
		if (wb_jwt) {
			config.headers['Authorization'] = `Bearer ${wb_jwt}`
		}
		return config
	}

	private _handleResponse = (response: AxiosResponse) => {
		return response.data || response
	}

	private _handleError = (error: AxiosError) => {
		return Promise.reject(error)
	}
}
